import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useOne } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Select from "components/select";
import Button from "components/button";
import { Link } from "react-router-dom";
import CONFIG from "app-config/config.json";
import { RefreshIcon } from "@heroicons/react/outline";
import { _generateSlug } from "utills/helpers";
import Dropzone from "components/dropzone";
import { IFipixEventDetail, IFipixEventForm, IOrgIdsItem } from "interfaces";
import moment from "moment";
import DatePicker from "components/datePicker";
import { get } from "lodash";

export interface FipixEventFormProps {
  id?: string;
  defaultData?: IFipixEventDetail;
  onSubmit: (data: IFipixEventForm) => void;
}

const FipixEventSchema = (id?: string) => {
  return z.object({
    is_active: z.boolean(),
    name_en: z.string().min(1, "This field is required"),
    name_vi: z.string().min(1, "This field is required"),
    banner_url: z.string().min(1, "This field is required"),
    organization_id: z.string().optional(),
    start_date: z.string().min(1, "This field is required"),
    slug: !id ? z.string().min(1, "This field is required") : z.any().optional(),
    hidden: z.boolean(),
    has_bib_recognition: z.boolean(),
    has_face_recognition: z.boolean(),
    show_all_images: z.boolean(),
  })
};

export const FipixEventForm = ({ id, defaultData, onSubmit }: FipixEventFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IFipixEventForm, HttpError, IFipixEventForm>({
    defaultValues: {
      is_active: defaultData?.is_active ?? false,
      name_en: defaultData?.name_en ?? "",
      name_vi: defaultData?.name_vi ?? "",
      banner_url: defaultData?.banner_url ?? "",
      organization_id: defaultData?.organization_id ?? "",
      start_date: defaultData?.start_date ?? "",
      slug: defaultData?.slug ?? "",
      hidden: defaultData?.hidden ?? false,
      has_bib_recognition: defaultData?.has_bib_recognition ?? true,
      has_face_recognition: defaultData?.has_face_recognition ?? true,
      show_all_images: defaultData?.show_all_images ?? true,
    },
    resolver: zodResolver(FipixEventSchema(id)),
  });

  const { data: orgIds, isFetching: isFetchingOrganization } = useOne<
    IOrgIdsItem[],
    HttpError
  >({
    resource: "organization/ids",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const organizationIds = (orgIds?.data ?? []).map((org: IOrgIdsItem) => ({
    value: org.id,
    label: org.name_en,
  }));

  const _renderGenerateSlugButton = () => {
    return ((watch("name_en") || watch("name_vi")) && !id) ? (
      <div className="w-8 h-full flex items-center justify-center absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer">
        <RefreshIcon
          className="w-5 text-brand-500"
          onClick={() =>
            setValue(
              "slug",
              _generateSlug(
                watch("name_en") || watch("name_vi")
              ),
              {shouldValidate: true}
            )
          }
        />
      </div>
    ) : <></>
  }

  const _submit = (values: IFipixEventForm) => {
    const submitValues = { ...values };
    submitValues["organization_id"] = submitValues["organization_id"] || null;
    submitValues["start_date"] = moment(submitValues["start_date"]).format("YYYY-MM-DD HH:mm:ss");
    onSubmit(submitValues);
  };

  return (
    <div className="w-full ">
      <div>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-8 lg:gap-x-6">
              <div className="col-span-full">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
              </div>
              <div className="sm:col-span-2">
                <Label id="has_face_recognition" name="Face recognition" />
                <Controller
                  control={control}
                  name="has_face_recognition"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="has_face_recognition"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        onChange(e.target.checked)
                      }    
                    />
                  )}
                />
              </div>
              <div className="sm:col-span-2">
                <Label id="has_bib_recognition" name="BIB recognition" />
                <Controller
                  control={control}
                  name="has_bib_recognition"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="has_bib_recognition"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        onChange(e.target.checked)
                      }    
                    />
                  )}
                />
              </div>
              <div className="sm:col-span-2">
                <Label id="hidden" name="Hidden" hint="If checked, the event will not be displayed on the Fipix homepage but the event details page can still be accessed" />
                <Controller
                  control={control}
                  name="hidden"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="hidden"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        onChange(e.target.checked)
                      }    
                    />
                  )}
                />
              </div>
              <div className="sm:col-span-2">
                <Label id="show_all_images" name="Show all images" />
                <Controller
                  control={control}
                  name="show_all_images"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="show_all_images"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        onChange(e.target.checked)
                      }    
                    />
                  )}
                />
              </div>
              <div className="sm:col-span-4">
                <Label required id="name_en" name="Event name eng" />
                <Input
                  id="name_en"
                  type="text"
                  placeholder="Event name eng"
                  error={errors["name_en"]}
                  {...register("name_en")}
                />
                {errors["name_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.name_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-4">
                <Label required id="name_vi" name="Event name vie" />
                <Input
                  id="name_vi"
                  type="text"
                  placeholder="Event name vie"
                  error={errors["name_vi"]}
                  {...register("name_vi")}
                />
                {errors["name_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.name_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-4">
                <Label id="organization_id" name="Organization" />
                <Controller
                  control={control}
                  name="organization_id"
                  render={({ field: { onChange, value = "", ref } }) => (
                    <Select
                      key="organization_id"
                      inputRef={ref}
                      options={organizationIds}
                      id="organization_id"
                      placeholder="Select Organization"
                      isClearable={true}
                      value={organizationIds.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      )}
                      onChange={(selected: { label: string; value: string } | null) =>
                        onChange(selected?.value ?? "")
                      }
                      error={errors["organization_id"]}
                      isLoading={isFetchingOrganization}
                    />
                  )}
                />
                {errors["organization_id"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.organization_id.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-4">
                <Label required id="slug" name="Event link" hint="Set up a link for your Detail page on Fipix Web and cannot be edited once created. Format: text-text-text-.... Ex: giai-chay-actiup" />
                <div className="flex">
                  <div className="max-w-[50%] inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm break-all">
                    <p className="line-clamp-1">{CONFIG.FIPIX_DOMAIN}/vi/</p>
                  </div>
                  <div className="relative flex-1">
                    <Input
                      id="slug"
                      className="rounded-l-none pr-10"
                      placeholder="slug-name"
                      error={errors["slug"]}
                      {...register("slug", {
                        disabled: id ? true : false
                      })}
                    />
                    {_renderGenerateSlugButton()}
                  </div>
                </div>
                {errors["slug"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.slug.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-4">
                <Label required id="start_date" name="Event date" />
                <Controller
                  control={control}
                  name="start_date"
                  render={({ field: { onChange, value = "" } }) => {
                    return (
                      <DatePicker
                        id="start_date"
                        placeholder="Event date"
                        className="bg-white"
                        error={get(
                          errors,
                          `start_date.message`,
                          ""
                        )}
                        value={value}
                        onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD"))}
                      />
                    );
                  }}
                />
                {errors["start_date"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.start_date.message}
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="col-span-full">
                <Label id="banner_url" name="Banner (1440x600)" required />
                <div>
                  <Controller
                    control={control}
                    name="banner_url"
                    render={({ field: { onChange, value = "" } }) => {
                      return (
                        <Dropzone
                          id="banner_url"
                          validator={{
                            width: 1440,
                            height: 600,
                          }}
                          onChange={(imageUrl: string) => onChange(imageUrl)}
                          value={value}
                        />
                      );
                    }}
                  />
                  <p className="mt-1 text-sm text-[#868E95]">
                    Recommended image size: 1440 x 600px <span className="mx-2">•</span> Maximum file size: 2MB <span className="mx-2">•</span> Supported image files: PNG, JPEG, JPG
                  </p>
                </div>
                {errors["banner_url"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["banner_url"].message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/fipix/events${id ? `/${id}` : ""}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit" onClick={handleSubmit(_submit)}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
